import React, { Suspense, lazy, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, Grid3x3, Link2, Shield } from 'lucide-react';
import { use3DTier } from '../../lib/site';
import { useNearViewport } from '../../hooks/useNearViewport';

const PanelScene = lazy(() => import('../three/PanelScene'));

const HOTSPOTS = [
  {
    key: 'core',
    icon: Layers,
    title: 'EPS CORE',
    text: 'Thermal insulation and reduced structural weight.',
  },
  {
    key: 'mesh',
    icon: Grid3x3,
    title: 'STEEL MESH',
    text: 'Reinforcement across both faces of the panel.',
  },
  {
    key: 'connectors',
    icon: Link2,
    title: 'CONNECTORS',
    text: 'Connect reinforcement layers through the EPS core.',
  },
  {
    key: 'skin',
    icon: Shield,
    title: 'STRUCTURAL SKIN',
    text: 'Concrete / mortar layers create the finished wall system.',
  },
];

const PanelFallback = () => (
  <div className="w-full h-full rounded-xl border border-border bg-[#FBF8F3] flex items-center justify-center">
    <div className="relative w-40 h-56">
      <div className="absolute inset-0 translate-x-4 rounded-sm bg-[#cfc9c0]" />
      <div className="absolute inset-0 translate-x-2 rounded-sm bg-[#f7f4ee] border border-border" />
      <div className="absolute inset-0 rounded-sm bg-[#cfc9c0]" />
      <p className="absolute -bottom-8 left-1/2 -translate-x-1/2 font-mono-tech text-[10px] tracking-widest uppercase text-muted-foreground whitespace-nowrap">
        EPS panel layers
      </p>
    </div>
  </div>
);

export const TechnologySection = () => {
  const [active, setActive] = useState(null);
  const tier = use3DTier();
  // Defer the three.js chunk + WebGL canvas until the section approaches the
  // viewport — keeps initial mobile load light.
  const [containerRef, near] = useNearViewport();

  return (
    <section id="technology" className="py-16 sm:py-20 lg:py-28" data-testid="technology-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <p className="font-mono-tech text-xs tracking-[0.28em] uppercase text-[#B87333]">01 — The Technology</p>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">
            ONE PANEL.
            <br />
            MULTIPLE FUNCTIONS.
          </h2>
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-12 gap-10 items-center">
          <div ref={containerRef} className="lg:col-span-6 h-[360px] sm:h-[440px]" data-testid="technology-3d-container">
            {tier === 'full' && near ? (
              <Suspense fallback={<PanelFallback />}>
                <PanelScene active={active} explode={!!active} />
              </Suspense>
            ) : (
              <PanelFallback />
            )}
          </div>

          <div className="lg:col-span-6">
            <div className="space-y-3">
              {HOTSPOTS.map((h) => {
                const Icon = h.icon;
                const isActive = active === h.key;
                return (
                  <button
                    key={h.key}
                    type="button"
                    data-testid={`technology-hotspot-${h.key === 'core' ? 'eps-core' : h.key}`}
                    onClick={() => setActive(isActive ? null : h.key)}
                    aria-pressed={isActive}
                    className={`select-card w-full text-left flex items-start gap-4 p-5 rounded-xl border ${
                      isActive
                        ? 'border-[#155E6D] bg-[#155E6D]/[0.04] shadow-[0_12px_40px_rgba(21,94,109,0.10)]'
                        : 'border-border bg-card hover:border-foreground/25'
                    }`}
                  >
                    <span
                      className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center border ${
                        isActive ? 'border-[#155E6D]/30 text-[#155E6D] bg-[#155E6D]/[0.06]' : 'border-border text-muted-foreground'
                      }`}
                    >
                      <Icon className="w-4.5 h-4.5" size={18} />
                    </span>
                    <span>
                      <span className={`font-mono-tech text-xs tracking-[0.2em] block ${isActive ? 'text-[#155E6D]' : 'text-foreground'}`}>
                        {h.title}
                      </span>
                      <span className="text-sm text-muted-foreground mt-1 block leading-relaxed">{h.text}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-6 text-xs text-muted-foreground font-mono-tech tracking-wide">
              Select a layer to separate the panel and inspect the system.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
